import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getConfig } from '../api/auth';
import { setReference } from '../features/reference/referenceSlice';

/**
 * Custom hook to automatically refresh config/reference data in the background
 * This ensures dropdown data is always up-to-date across all screens
 *
 * @param {number} refreshIntervalMinutes - Interval in minutes to refresh config (default: 1)
 */
const useConfigRefresh = (refreshIntervalMinutes = 1) => {
  const dispatch = useDispatch();
  const intervalRef = useRef(null);
  const isRefreshing = useRef(false);
  const referenceData = useSelector((state) => state.reference);

  const refreshConfig = useCallback(async () => {
    // Prevent concurrent refresh calls
    if (isRefreshing.current) {
      console.log('Config refresh already in progress, skipping...');
      return;
    }

    isRefreshing.current = true;

    try {
      console.log('Refreshing config data at:', new Date().toLocaleTimeString());
      const res = await getConfig();

      if (res?.data?.data) {
        // Extract the actual config data from the 'value' property
        // API returns: { id, client, company, type, value: {...actual config...} }
        // We only want to store the value object
        const configData = res.data.data.value || res.data.data;

        // Update localStorage
        localStorage.setItem('reference', JSON.stringify(configData));
        // Update Redux store
        dispatch(setReference(configData));
        console.log('Config data refreshed successfully');
      } else {
        console.warn('Config API returned empty data');
      }
    } catch (error) {
      console.error('Failed to refresh config data:', error.message);
      // Don't clear existing data on error - keep using cached data
    } finally {
      isRefreshing.current = false;
    }
  }, [dispatch]);

  // Check for initial data only once on mount
  const hasInitialData = useRef(
    referenceData && (
      referenceData.gender?.length > 0 ||
      referenceData.countries?.length > 0 ||
      referenceData.technologies?.technology?.length > 0 ||
      referenceData.jobtitles?.job?.length > 0
    )
  );

  useEffect(() => {
    // Fetch immediately only if no data on initial mount
    if (!hasInitialData.current) {
      console.log('No config data found, fetching immediately...');
      refreshConfig();
    }

    // Set up interval to refresh config periodically
    const intervalMs = refreshIntervalMinutes * 60 * 1000;
    intervalRef.current = setInterval(refreshConfig, intervalMs);

    console.log(`Config refresh scheduled every ${refreshIntervalMinutes} minute(s)`);

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        console.log('Config refresh interval cleared');
      }
    };
  }, [refreshConfig, refreshIntervalMinutes]); // Removed referenceData from deps

  // Return the refresh function so components can manually trigger a refresh if needed
  return { refreshConfig, referenceData };
};

export default useConfigRefresh;
